import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Box, Flex, Text, VStack, Code, Skeleton, Stack } from "@chakra-ui/react";
import { useAppContext } from "../contexts/app/index";
import { setCxIsRoot } from "../shared/state-management";
import {
  assignRoot,
  unassignRoot,
  getCustomers,
  type GetCustomersParams,
} from "../../api/customer";
import { type CustomerMapEntry, createCustomerAndRootMaps } from "../../domain/customer";
import { roleAdmin, areRolesIncluded } from "../../shared/access-control";
import { Accordion } from "../shared/Accordion";
import { TagBoolean } from "../shared/Tag";
import { ButtonBoolean } from "../shared/Button";
import { DebouncedSearchInput } from "../shared/debounced-search-input";
import { ServerSideFilter, type FilterDef } from "../shared/server-side-filter";
import { captureAndDisplayError } from "../shared/util";
import useMetriportToast from "../shared/toast";

const searchDebounceMs = 300;
const searchMaxLength = 100;
const defaultCustomerFilters = { isRoot: true };

const customerListFilterConfig: FilterDef[] = [
  {
    type: "multiSelect",
    paramKey: "subscriptionStatus",
    label: "Subscription status",
    options: [
      { value: "active", label: "Active" },
      { value: "disabled", label: "Disabled" },
    ],
  },
  {
    type: "booleanPair",
    paramKey: "isRoot",
    label: "Customer type",
    trueLabel: "Root",
    falseLabel: "Non-root",
  },
];

function toGetCustomersParams(params: Record<string, unknown>): GetCustomersParams {
  const out: GetCustomersParams = {};
  const sub = params.subscriptionStatus;
  if (Array.isArray(sub) && sub.length > 0) {
    out.subscriptionStatus = sub.filter((s): s is string => typeof s === "string");
  }
  if (params.isRoot === true || params.isRoot === false) {
    out.isRoot = params.isRoot as boolean;
  }
  return out;
}

function paramsMatchContext(params: Record<string, unknown>): boolean {
  if (params.isRoot !== true) return false;
  const sub = params.subscriptionStatus;
  return sub === undefined || (Array.isArray(sub) && sub.length === 0);
}

const emptyListData: ReturnType<typeof createCustomerAndRootMaps> = {
  customerMap: {},
  rootMap: {},
};

const customersQueryKeyBase = ["customers"] as const;

export default function HomePage() {
  const { state, dispatch } = useAppContext();
  const navigate = useNavigate();
  const toast = useMetriportToast();
  const queryClient = useQueryClient();

  const [filterParams, setFilterParams] = useState<Record<string, unknown>>(defaultCustomerFilters);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const isSearchActive = debouncedSearch.length > 0;
  const useContextData = paramsMatchContext(filterParams);
  const filterApiParams = toGetCustomersParams(filterParams);
  const trimmedSearch = debouncedSearch.trim();
  const apiParams: GetCustomersParams = {
    ...filterApiParams,
    search: trimmedSearch.length > 0 ? trimmedSearch : undefined,
  };

  const customersQueryKey = [...customersQueryKeyBase, apiParams] as const;

  const { data: listData, isLoading: isLoadingList } = useQuery({
    queryKey: customersQueryKey,
    queryFn: async () => {
      const customers = await getCustomers(apiParams);
      return createCustomerAndRootMaps(customers);
    },
    enabled: isSearchActive || !useContextData,
    placeholderData: (previousData: ReturnType<typeof createCustomerAndRootMaps> | undefined) =>
      previousData,
    meta: {
      toast,
      errorMessage: "Failed to load customers",
    },
  });

  const contextDisplayData = {
    customerMap: state.customerMap ?? {},
    rootMap: state.rootMap ?? {},
  };
  const displayData =
    isSearchActive || !useContextData ? listData ?? emptyListData : contextDisplayData;

  const rootChangeMutation = useMutation({
    mutationFn: async ({ cxId, isRoot }: { cxId: string; isRoot: boolean }) => {
      if (isRoot) await unassignRoot(cxId);
      else await assignRoot(cxId);
    },
    onSuccess: (_, { cxId, isRoot }) => {
      setCxIsRoot({
        cxId,
        state,
        dispatch,
        isRoot: !isRoot,
      });
      queryClient.invalidateQueries({ queryKey: customersQueryKey });
    },
    onError: (error, { cxId, isRoot }) => {
      captureAndDisplayError({
        error,
        msg: "Failed to update root status",
        context: "handleIsRootChange",
        captureMsg: "root status update failed",
        toast,
        extras: { cxId, isRoot },
      });
    },
  });

  function handleIsRootChange(cxId: string, isRoot: boolean) {
    rootChangeMutation.mutate({ cxId, isRoot });
  }

  return (
    <Box>
      <VStack p={10} align="start">
        {!state.isLoaded ? (
          <Skeleton mt={5} h={100} w={"4xl"} />
        ) : (
          <>
            {state.userRoles.length > 0 ? (
              <Box>
                <Flex mb={5} align="center" gap={3} w="full">
                  <Text fontWeight={"bold"}>Customer list</Text>
                  <DebouncedSearchInput
                    placeholder="Search by id, email, name…"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onDebouncedChange={setDebouncedSearch}
                    debounceMs={searchDebounceMs}
                    maxLength={searchMaxLength}
                    size="sm"
                    maxW="320px"
                  />
                  <ServerSideFilter
                    filters={customerListFilterConfig}
                    value={filterParams}
                    defaultValue={defaultCustomerFilters}
                    onChange={setFilterParams}
                    isDisabled={isSearchActive}
                  />
                </Flex>
                <Box>
                  {(isSearchActive || !useContextData) && isLoadingList ? (
                    <Skeleton mt={2} h={200} w="4xl" />
                  ) : (
                    <Accordion
                      id="customers"
                      entries={
                        Object.entries(displayData.customerMap)
                          .filter(
                            (entry): entry is [string, CustomerMapEntry] =>
                              entry[1] !== undefined && entry[1].rootCustomer.email !== null
                          )
                          .sort(
                            (a, b) =>
                              (a[1]?.rootCustomer.isRoot ? 0 : 1) -
                              (b[1]?.rootCustomer.isRoot ? 0 : 1)
                          ) as [string, object][]
                      }
                      getLabel={(payload: object) => {
                        const castPayload = payload as CustomerMapEntry;
                        return (
                          <Flex w={"full"} justifyContent={"space-between"}>
                            <Text>{castPayload.rootCustomer.email}</Text>
                            <Text>{castPayload.rootCustomer.id}</Text>
                          </Flex>
                        );
                      }}
                      labelOnClick={(payload: object) => {
                        const castPayload = payload as CustomerMapEntry;
                        navigate(`/customer/${castPayload.rootCustomer.id}`);
                      }}
                      getLabelActions={(payload: object) => {
                        const castPayload = payload as CustomerMapEntry;
                        const rootMapToShow = displayData.rootMap;
                        const isRoot = rootMapToShow[castPayload.rootCustomer.id];
                        if (isRoot !== undefined) {
                          return (
                            <>
                              {areRolesIncluded([roleAdmin], state.userRoles) ? (
                                <ButtonBoolean
                                  id="isRoot"
                                  check={isRoot}
                                  disableOnClick={false}
                                  onClick={() =>
                                    handleIsRootChange(castPayload.rootCustomer.id, isRoot)
                                  }
                                  label="Root Status"
                                  activeColor="green"
                                  confirmTitle={`${isRoot ? "Disable" : "Enable"} root status?`}
                                  confirmMessage={`Are you sure you want to ${
                                    isRoot ? "disable" : "enable"
                                  } root status for this customer?`}
                                  confirmText={isRoot ? "Disable" : "Enable"}
                                  shouldAskForConfirmation
                                />
                              ) : (
                                <TagBoolean id="isRoot" check={isRoot} label={`Root Status`} />
                              )}
                              <TagBoolean
                                id="isActiveSub"
                                check={castPayload.rootCustomer.subscriptionStatus === "active"}
                                label={`Sub Active`}
                              />
                            </>
                          );
                        }
                      }}
                      getPanelInfo={(payload: object) => {
                        const castPayload = payload as CustomerMapEntry;
                        return (
                          <Box mt={10} pl={5}>
                            {castPayload.connectedEmails.length === 0 ? (
                              <Code>No associated users</Code>
                            ) : (
                              <Stack mb={2} direction="column">
                                <Code mb={1}>Associated Users</Code>
                                {castPayload.connectedEmails.map(email => {
                                  return <Code key={email}>{email}</Code>;
                                })}
                              </Stack>
                            )}
                          </Box>
                        );
                      }}
                      getPanelInfoJsonExcludeKeys={["connectedEmails"]}
                    />
                  )}
                </Box>
              </Box>
            ) : (
              <Text>
                You have not been added to a group to enable access. Please contact an admin.
              </Text>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
}
